<script setup>
import _ from 'lodash'
import UserInfoExtended from './UserInfoExtended'
import OccupationProgress from './OccupationProgress'
import Presence from './Presence'
import Absence from './Absence'
import Break from './Break'
import { defaultEmployeeImage, toHours } from '../mixin/common'
import moment from "moment";
import { computed, ref, onMounted } from "vue";
import { ColdDrink } from "@element-plus/icons-vue";

const props = defineProps({
  info: {
    type: Object,
    required: true
  },
  isSingleDay: {
    type: Boolean,
    default: true
  },
  isAbsenceActive: {
    type: Boolean,
    default: false
  }
})

const dialogVisible = ref(false);
const baseUri = process.env.VUE_APP_BASE_URI;
// const holidaysFrom = ref('');
// const holidaysTo = ref('');
// const publicHoliday = ref({});

// eslint-disable-next-line no-unused-vars
const isCollaborator = computed(() => {
  return props.info.employee && props.info.employee.employeeType && props.info.employee.employeeType === "external_collaborator"
})

onMounted(() => {
  // if (props.info.holidays && props.info.holidays.fromDate && props.info.holidays.toDate) {
  //   holidaysFrom.value = props.info.holidays.fromDate;
  //   holidaysTo.value = props.info.holidays.toDate;
  // }
  // if (props.info.publicHoliday && props.info.publicHoliday.date) {
  //   publicHoliday.value = props.info.publicHoliday;
  // }
})

const headerStyle = computed(() => {
  if (!isWorking()) {
    if (isResting() || isRestingPublicHoliday()) {
      return {
        'border-top': '#808080' + ' solid 4px',
        'background': '#f5f4f4',
      };
    } else if (isAbsence()) {
      return {
        'border-top': 'cyan' + ' solid 4px',
        'background': 'lightcyan',
      };
    } else if (isOnBreak()){
      return {
        'border-top': '#b8d9ec' + ' solid 4px',
        'background': '#eaf6ff',
      };
    } else {
      return {};
    }
  }
  return { 'border-top': getWorklog()?.color + ' solid 4px' };
})
const project = computed(() => {
  return getWorklog()?.projectName ?? 'Sin proyecto';
})
const client = computed(() => {
  return getWorklog()?.clientName ?? 'Sin cliente';
})
const timeentry = computed(() => {
  return getWorklog()?.description;
})
const aPhoto = computed(() => {
  if (!props.info?.employee?.photo) {
    return null;
  }
  return baseUri.replace(/\/$/, "") + '/images/employees/' + props.info.employee.photo;
})
const userFullName = computed(() => {
  return `${props.info?.employee?.name} ${props.info.employee.lastname}`;
})
const totalSignins = computed(() => {
  return toHours(props.info?.presence?.totalWorked ?? 0);
})
const totalWorklog = computed(() => {
  return toHours(props.info?.totalWorked);
})
const occupationPercent = computed(() => {
  const val = parseFloat(((props.info.totalWorked * 100) / (props.info.presence?.totalWorked ?? 0)).toFixed(2));
  return isNaN(val) ? 0 : val;
})
const isFulledOccupationPercent = computed(() => {
  return occupationPercent.value > 100;
})
const isOnVacations = computed(() => {
  return isResting();
})
const isOnPublicHoliday = computed(() => {
  return isRestingPublicHoliday();
})
const absences = computed(() => {
  return isAbsence();
})

const breaks = computed(()=>{
  return isOnBreak()
})


const isWorking = () => {
  return getWorklog() && (getWorklog()?.start !== null) && (getWorklog()?.end === null)
}
const isResting = () => {
  // holidaysFrom.value = props.info.holidays.fromDate;
  // holidaysTo.value = props.info.holidays.toDate;
  return props.info?.holidays?.length !== 0;
}
const isRestingPublicHoliday = () => {
  // publicHoliday.value = props.info.publicHoliday;
  return props.info?.publicHoliday?.length !== 0;
}
const isAbsence = () => {
  return props.isAbsenceActive;
}
const isOnBreak= () => {
  return props.info?.presence?.onBreak;
}
// eslint-disable-next-line no-unused-vars
const size = (object) => {
  return _.size(object);
}

// eslint-disable-next-line no-unused-vars
const getWorklog = () => {
  if (props.info.employee.canCreateTimeEntry) {
    return props.info.worklogTrackerNet?.[0]
  } else {
    return props.info.worklog?.[0]
  }
}


const formatDateAndMonth = (value) => {
  if (!value) return '';

  // Array de formatos posibles
  const formats = [
    'YYYY-MM-DD',
    'DD-MM-YYYY',
    'MM-DD-YYYY',
  ];

  const m = moment(value, formats, true);
  if (!m.isValid()) return '';

  return m.format('DD-MM');
}


</script>

<template>
  <div>
    <el-card
      shadow="hover"
      :style="headerStyle"
      :class="['employee-info-card', { 'employee-info-card--break': breaks }]"
    >
      <template #header>
        <div class="user-info-header">
          <el-avatar class="user-info-avatar" :src="aPhoto">
            <img :src="defaultEmployeeImage" alt="Default Image">
          </el-avatar>
          <span class="user-info-name" style="margin-left: 10px; font-size: 14px;">
            {{ userFullName }}

          </span>
          <span v-if="breaks" class="user-info-break-badge">PAUSA</span>
          <span class="user-info-summary" style="margin-left: auto">
            <el-button size="small" @click="dialogVisible = true" style="max-width: 100%; margin-right: -10px;">
              <div v-if="!isCollaborator && isSingleDay">
                <font-awesome-icon icon="thumbs-up" /> {{ totalSignins }} /
              </div>
              <div>
                <font-awesome-icon icon="clock" />{{ totalWorklog }}
              </div>
            </el-button>
            <OccupationProgress :percentaje="occupationPercent" :isFulled="isFulledOccupationPercent"
              v-if="!isCollaborator" />
          </span>
        </div>
      </template>
      <div v-if="!isOnVacations && !isOnPublicHoliday">
        <el-divider>{{ info.employee.canCreateTimeEntry ? 'Trackernet' : 'Clockify' }}</el-divider>
        <i class="el-icon-s-cooperation" /> {{ client }} - <i class="el-icon-s-claim" /> {{ project }}<br>
        <small> {{ timeentry }} </small>
      </div>
      <div v-else-if="isOnVacations">
        <el-icon color="#60626600">
          <ColdDrink />
        </el-icon> De vacaciones desde {{ formatDateAndMonth(info.holidays.fromDate) }} hasta {{
          formatDateAndMonth(info.holidays.toDate) }}<br>
        <!--        </el-icon> De vacaciones desde {{ formatDateAndMonth(holidaysFrom) }} hasta {{ formatDateAndMonth(holidaysTo) }}<br>-->
      </div>
      <div v-else-if="isOnPublicHoliday">
        <span v-if="info.publicHoliday.type === 'nacional'">
          <el-icon color="#60626600">
            <ColdDrink />
          </el-icon> {{ formatDateAndMonth(info.publicHoliday.date) }} {{ info.publicHoliday.description }}, Fiesta
          Nacional<br>
          <!--        </el-icon>  {{ formatDateAndMonth(publicHoliday.date) }} {{ publicHoliday.description }}, Fiesta Nacional<br>-->
        </span>
        <span v-else>
          <el-icon color="#60626600">
            <ColdDrink />
          </el-icon> {{ formatDateAndMonth(info.publicHoliday.date) }} {{ info.publicHoliday.description }} en {{
            info.publicHoliday.workPlaceName }}<br>
          <!--          </el-icon> {{ formatDateAndMonth(publicHoliday.date) }} {{ publicHoliday.description }} en {{ publicHoliday.workPlaceName }}<br>-->
        </span>
      </div>
      <div v-if="!isOnVacations && !isOnPublicHoliday && !isCollaborator && isSingleDay">
        <el-divider>Presencia</el-divider>
        <presence :presence="info.presence" />
      </div>
      <div v-else-if="!isOnVacations && !isOnPublicHoliday && !isCollaborator">
        <el-divider>Presencia</el-divider>
        <span class="presence-report-link">Ver informe presencia</span>
      </div>

      <div v-if="absences">
        <el-divider>Ausencia</el-divider>

        <Absence :absence="info.absence" />
      </div>
      <div v-if="breaks">
        <Break :break="info.presence" />
      </div>
    </el-card>
    <el-dialog v-model="dialogVisible" title="Información de empleado" class="employee-info-dialog el-dialog-production-dashboard">
      <user-info-extended :info="info" />
    </el-dialog>
  </div>
</template>

<style lang="scss">
.user-info-header {
  display: flex;
  align-items: center;
}

.user-info-name {
  display: inline-block;
}

.presence-report-link {
  color: #0054a6;
  font-weight: 600;
}

.user-info-avatar {
  min-width: 40px;
  min-height: 40px;
}

.el-table.condensed-table td,
.el-table.condensed-table th {
  padding: 3px;
}

.employee-info-dialog {
  &.el-dialog {
    display: flex;
    flex-direction: column;
    margin-top: 15vh !important;
  }

  .el-dialog__body {
    background: #f3f0fb !important;
    padding: 20px !important;
    overflow: visible !important;
    min-height: unset !important;
    /* Esto eliminará cualquier min-height previo */
  }
}

.employee-info-card {

  // .el-card__header{
  //   border-bottom: none;
  // }
  .el-card__body {
    padding-bottom: 10px;
  }
}

@media (max-width: 1440px) {
  .user-info-header {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
  }

  .user-info-name {
    flex: 1 1 140px;
    margin-left: 8px !important;
    min-width: 0;
    line-height: 1.25;
  }

  .user-info-summary {
    display: flex;
    flex: 1 1 100%;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-left: 0 !important;
    margin-top: 8px;
    min-width: 0;
    width: 100%;
  }

  .user-info-summary .el-button {
    max-width: fit-content;
    margin-right: 0 !important;
  }
}

.employee-info-card--break {
  color: #1f2933;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(40, 76, 106, 0.16);
  overflow: hidden;

  .el-card__header {
    background: #d9edf8;
    border-bottom: 0;
    padding: 18px 24px 10px;
  }

  .el-card__body {
    background: #eaf6ff;
    padding: 18px 24px 22px;
  }

  .user-info-header {
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 14px;
  }

  .user-info-avatar {
    min-width: 40px;
    min-height: 40px;
    width: 40px;
    height: 40px;
  }

  .user-info-name {
    flex: 1 1 0;
    margin-left: 10px !important;
    min-width: 0;
    font-size: 14px !important;
    line-height: 1.25;
  }

  .user-info-break-badge {
    align-self: center;
    background: #0054a6;
    border-radius: 5px;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1.5px;
    line-height: 1;
    padding: 8px 14px;
    text-align: center;
    white-space: nowrap;
  }

  .user-info-summary {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    align-items: center;
    gap: 12px;
    flex: 0 0 100%;
    margin: 14px 0 0 !important;
    min-width: 0;
    padding: 0;
    width: 100%;
  }

  .user-info-summary .el-button {
    grid-column: 2;
    grid-row: 1;
    background: transparent;
    border: 0;
    color: #1f2933;
    font-size: 14px;
    height: auto;
    margin-right: 0 !important;
    min-width: max-content;
    padding: 0;
  }

  .user-info-summary .el-button > span {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .user-info-summary .el-button div {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .user-info-summary .el-progress {
    grid-column: 1;
    grid-row: 1;
    min-width: 0;
    width: 100%;
  }

  .user-info-summary .el-progress-bar__outer {
    background-color: #f1f7fb;
  }

  .el-divider {
    color: #111827;
    font-size: 16px;
    line-height: 1.2;
    margin: 24px 0;
  }

  .el-card__body > div:first-child .el-divider {
    margin-top: 0;
  }

  .el-divider__text {
    background: #eaf6ff;
    padding: 0 20px;
  }

  .short-divider {
    margin: 20px auto;
    width: 80%;
  }

  .el-table.condensed-table th,
  .el-table.condensed-table td {
    background: transparent;
    color: #111827;
  }

  .el-table.condensed-table th {
    font-weight: 500;
  }
}

@media (max-width:768px){
  .employee-info-dialog {
    .el-dialog__body {
      padding: 10px !important;
    }
  }
}

@media (max-width: 767px) {
  .employee-info-card--break {
    .el-card__header {
      padding: 14px 18px 10px;
    }

    .el-card__body {
      padding: 16px 18px 18px;
    }

    .user-info-header {
      gap: 10px 12px;
    }

    .user-info-name {
      flex-basis: 0;
      font-size: 14px !important;
    }

    .user-info-break-badge {
      font-size: 13px;
      letter-spacing: 1.4px;
      padding: 8px 12px;
    }

    .user-info-summary {
      grid-template-columns: minmax(0, 1fr);
      gap: 10px;
      margin: 12px 0 0 !important;
      padding: 0;
      width: 100%;
    }

    .user-info-summary .el-button,
    .user-info-summary .el-progress {
      grid-column: 1;
    }

    .user-info-summary .el-progress {
      grid-row: 1;
    }

    .user-info-summary .el-button {
      font-size: 14px;
      grid-row: 2;
      justify-self: center;
    }

    .user-info-summary .el-progress {
      max-width: none;
    }

    .el-divider {
      font-size: 16px;
      margin: 24px 0;
    }

    .el-table.condensed-table th,
    .el-table.condensed-table td {
      padding: 3px;
    }
  }
}

</style>
